/**
 * cjCelebrate — central place to fire CJ moments: confetti, sound, and the
 * Sonner toast with CJ's avatar.
 *
 *   cjCelebrate({ type, title, subtitle, intensity })
 *
 *   type:        "org_first_story" | "org_milestone" | "personal_points" |
 *                "badge" | "kudos_sent" | "story_submitted" | "story_approved" |
 *                "leaderboard_climb"
 *   intensity:   "subtle" | "normal" | "big" | "epic"  (affects confetti & sound)
 *   title:       short header in the toast
 *   subtitle:    optional body line
 *   icon:        optional emoji to lead with (defaults to 🧡)
 */
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { CJ_AVATAR_URL } from "./constants";

const INTENSITY = {
  subtle: { particles: 30, spread: 50, scalar: 0.7, duration: 1, sound: false },
  normal: { particles: 80, spread: 65, scalar: 0.9, duration: 1.5, sound: true },
  big:    { particles: 180, spread: 85, scalar: 1.1, duration: 2.4, sound: true },
  epic:   { particles: 350, spread: 120, scalar: 1.3, duration: 4, sound: true },
};

const COLOR_POOLS = [
  ["#f68b1f", "#ff6b35", "#ffd400", "#7ac143", "#0091be", "#083050"],
];

// Web Audio synth — no external file. Plays a quick happy "ding" chord.
function playDing(intensity = "normal") {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.gain.value = 0.0001;
    gain.connect(ctx.destination);

    const notes = intensity === "epic"
      ? [523.25, 659.25, 783.99, 1046.5]   // C5 E5 G5 C6
      : intensity === "big"
      ? [523.25, 659.25, 783.99]            // C major triad
      : [659.25, 783.99];                   // E5 G5

    const dur = intensity === "epic" ? 0.9 : 0.55;
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = freq;
      const g = ctx.createGain();
      g.gain.value = 0.0001;
      osc.connect(g);
      g.connect(gain);
      const start = now + i * 0.07;
      g.gain.exponentialRampToValueAtTime(0.18, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
      osc.start(start);
      osc.stop(start + dur + 0.05);
    });
    gain.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + dur + 0.1);
    setTimeout(() => ctx.close(), (dur + 0.3) * 1000);
  } catch { /* silent */ }
}

function fireConfetti(intensity) {
  const cfg = INTENSITY[intensity] || INTENSITY.normal;
  const colors = COLOR_POOLS[0];
  const end = Date.now() + cfg.duration * 1000;

  const launch = () => {
    confetti({
      particleCount: cfg.particles,
      angle: 60,
      spread: cfg.spread,
      origin: { x: 0, y: 0.6 },
      colors,
      scalar: cfg.scalar,
    });
    confetti({
      particleCount: cfg.particles,
      angle: 120,
      spread: cfg.spread,
      origin: { x: 1, y: 0.6 },
      colors,
      scalar: cfg.scalar,
    });
    if (intensity === "epic" || intensity === "big") {
      confetti({
        particleCount: Math.floor(cfg.particles * 0.6),
        angle: 90,
        spread: 100,
        origin: { x: 0.5, y: 0.1 },
        colors,
        scalar: cfg.scalar,
      });
    }
  };

  launch();
  if (intensity === "epic") {
    const interval = setInterval(() => {
      if (Date.now() > end) return clearInterval(interval);
      launch();
    }, 600);
  }
}

const CJ_BODY = (subtitle) => subtitle || "CJ here — keep going.";

export function cjCelebrate({ type, title, subtitle, intensity = "normal", icon = "🧡" } = {}) {
  // Sound + confetti
  if (intensity !== "subtle") {
    fireConfetti(intensity);
    if (INTENSITY[intensity]?.sound !== false) playDing(intensity);
  }
  // Toast with CJ avatar
  const dur = intensity === "epic" ? 9000 : intensity === "big" ? 7000 : 5000;
  toast.custom(
    (t) => (
      <div className="bg-white rounded-2xl shadow-2xl border-2 border-orange-300 p-3 pr-4 flex items-center gap-3 min-w-[280px] max-w-[360px]" data-testid={`cj-celebrate-${type}`}>
        <img src={CJ_AVATAR_URL} alt="CJ" className="w-10 h-10 rounded-full shrink-0 ring-2 ring-orange-200" />
        <div className="min-w-0 flex-1">
          <div className="font-display text-sm leading-tight text-tc-ink">{icon} {title}</div>
          <div className="text-xs text-slate-500 mt-0.5 leading-snug">{CJ_BODY(subtitle)}</div>
        </div>
        <button
          onClick={() => toast.dismiss(t)}
          className="text-slate-400 hover:text-tc-ink text-lg leading-none"
          aria-label="Close"
        >×</button>
      </div>
    ),
    { duration: dur }
  );
}

/** Small helper: fire a CJ moment for a list of newly-awarded badges. */
export function cjBadgeBurst(newBadges = []) {
  newBadges.forEach((b, idx) => {
    setTimeout(() => {
      cjCelebrate({
        type: "badge",
        title: `New badge: ${b.title} ${b.emoji}`,
        subtitle: b.criteria,
        intensity: "big",
        icon: b.emoji || "🏅",
      });
    }, idx * 700);
  });
}

/** Lighter-weight celebrations for in-app actions. */
export function cjReact(message, opts = {}) {
  cjCelebrate({
    type: opts.type || "react",
    title: message,
    subtitle: opts.subtitle,
    intensity: opts.intensity || "subtle",
    icon: opts.icon || "🧡",
  });
}
